import CookieService from "../../../../../../common/ts/services/CookieService";
import {EmailCaptureApiCaller} from "../../../EmailCaptureApiCaller";
import {SERP_INTERACTION_FLAG} from "../../../SerpInteraction";

const BOTTOM_MODAL_DISMISSED_COOKIE = 'nestoria_bottom_modal_dismissed';
const COOKIE_EXPIRY_DAYS = 365;

export class BottomModal {
    private readonly modal: HTMLElement | null;
    private readonly form: HTMLFormElement | null;
    private readonly formContainer: HTMLElement | null;
    private readonly errorElement: HTMLElement | null;
    private readonly emailInput: HTMLInputElement | null;
    private readonly successContainer: HTMLElement | null;

    private readonly cookieService: CookieService;
    private onSuccessCallback: (() => void) | null = null;
    private onDismissCallback: (() => void) | null = null;

    constructor(private readonly apiCaller: EmailCaptureApiCaller) {
        this.modal = document.querySelector('.js-serp-email-bottom-modal');
        this.form = this.modal?.querySelector('.js-serp-email-bottom-modal-form') as HTMLFormElement | null;
        this.formContainer = this.modal?.querySelector('.js-serp-email-bottom-modal-form-container') as HTMLElement | null;
        this.errorElement = this.modal?.querySelector('.js-serp-email-bottom-modal-error') as HTMLElement | null;
        this.emailInput = this.modal?.querySelector('.js-serp-email-bottom-modal-input') as HTMLInputElement | null;
        this.successContainer = this.modal?.querySelector('.js-serp-email-bottom-modal-success') as HTMLElement | null;

        this.cookieService = new CookieService();
    }

    init(): void {
        if (!this.modal) return;

        this.prefillEmail();
        this.registerCloseEvents();
        this.registerFormSubmit();
    }

    initAutoShow(): void {
        if (this.hasDismissedCookie() || sessionStorage.getItem(SERP_INTERACTION_FLAG)) {
            return;
        }

        setTimeout(() => this.show(), 1000);
    }

    isAvailable(): boolean {
        return this.modal !== null;
    }

    onSuccess(callback: () => void): void {
        this.onSuccessCallback = callback;
    }

    onDismiss(callback: () => void): void {
        this.onDismissCallback = callback;
    }

    show(): void {
        if (!this.modal || this.hasDismissedCookie()) return;

        this.modal.classList.add('visible');

        this.prefillEmail();
    }

    hide(): void {
        if (!this.modal) return;

        this.modal.classList.remove('visible');
        this.setDismissedCookie();
        this.onDismissCallback?.();
    }

    private prefillEmail(): void {
        const savedEmail = this.apiCaller.getEmailFromStorage();
        if (savedEmail && this.emailInput) {
            this.emailInput.value = savedEmail;
        }
    }

    private hasDismissedCookie(): boolean {
        return CookieService.exists(BOTTOM_MODAL_DISMISSED_COOKIE);
    }

    private setDismissedCookie(): void {
        this.cookieService.setExpirableCookie(BOTTOM_MODAL_DISMISSED_COOKIE, 'true', COOKIE_EXPIRY_DAYS);
    }

    private registerCloseEvents(): void {
        const closeButton = this.modal?.querySelector('.js-serp-email-bottom-modal-close');
        closeButton?.addEventListener('click', () => this.hide());

        const denyButton = this.modal?.querySelector('.js-serp-email-bottom-modal-deny');
        denyButton?.addEventListener('click', () => this.hide());

        const okButton = this.modal?.querySelector('.js-serp-email-bottom-modal-ok');
        okButton?.addEventListener('click', () => this.hide());
    }

    private registerFormSubmit(): void {
        if (!this.form) return;

        this.form.addEventListener('submit', async (e: Event) => {
            e.preventDefault();

            const email = this.emailInput?.value?.trim();

            if (!email || !this.apiCaller.isValidEmail(email)) {
                this.showError();
                return;
            }

            this.hideError();

            const submitButton = this.form!.querySelector('.js-serp-email-bottom-modal-submit') as HTMLButtonElement;
            submitButton.disabled = true;

            try {
                const alertParams = this.apiCaller.getAlertParamsFromContainer(this.modal, this.form);
                await this.apiCaller.submitAlert(email, alertParams);
                this.apiCaller.saveEmailToStorage(email);
                this.showSuccess();
            } catch (error) {
                this.showError();
                submitButton.disabled = false;
            }
        });
    }

    private showSuccess(): void {
        if (this.formContainer) this.formContainer.style.display = 'none';
        if (this.successContainer) this.successContainer.classList.add('visible');

        this.onSuccessCallback?.();
    }

    private showError(): void {
        this.apiCaller.toggleError(this.errorElement, this.emailInput, true);
    }

    private hideError(): void {
        this.apiCaller.toggleError(this.errorElement, this.emailInput, false);
    }
}
